import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["rental", "startDate", "endDate", "message", "state", "details", "submit", "dateError", "submitContainer", "guestCount", "specialRequests", "submitButton", "adminOverride"]
  static values = {
    available: Boolean
  }

  connect() {
    if (this.hasStartDateTarget && this.hasEndDateTarget) {
      this.validateDates()
    }
    
    // Check if we already have availability state
    if (this.hasStateTarget) {
      const stateDiv = this.stateTarget.querySelector('div')
      if (stateDiv && stateDiv.dataset.available === 'true' && this.hasSubmitButtonTarget) {
        this.submitButtonTarget.disabled = false
      }
    }
  }

  validateDates() {
    if (!this.hasStartDateTarget || !this.hasEndDateTarget) return true

    const startDate = this.parseDate(this.startDateTarget.value)
    const endDate = this.parseDate(this.endDateTarget.value)
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    if (this.hasDateErrorTarget) {
      this.dateErrorTarget.classList.add('hidden')
    }

    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = false
    }

    if (!startDate || !endDate) {
      if (this.hasDateErrorTarget) {
        this.dateErrorTarget.textContent = "Please enter valid dates in MM/DD/YYYY or YYYY-MM-DD format"
        this.dateErrorTarget.classList.remove('hidden')
      }
      if (this.hasSubmitTarget) {
        this.submitTarget.disabled = true
      }
      return false
    }

    if (startDate < today) {
      if (this.hasDateErrorTarget) {
        this.dateErrorTarget.textContent = "Check-in date cannot be in the past"
        this.dateErrorTarget.classList.remove('hidden')
      }
      if (this.hasSubmitTarget) {
        this.submitTarget.disabled = true
      }
      return false
    }

    if (endDate < startDate) {
      if (this.hasDateErrorTarget) {
        this.dateErrorTarget.textContent = "Check-out date must be after check-in date"
        this.dateErrorTarget.classList.remove('hidden')
      }
      if (this.hasSubmitTarget) {
        this.submitTarget.disabled = true
      }
      return false
    }

    return true
  }

  parseDate(dateString) {
    if (!dateString) return null
    
    try {
      // Try parsing as ISO date string (YYYY-MM-DD)
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-').map(Number)
        return new Date(year, month - 1, day)
      }
      
      // Try parsing as MM/DD/YYYY
      if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString)) {
        const [month, day, year] = dateString.split('/').map(Number)
        return new Date(year, month - 1, day)
      }
      
      console.error(`Failed to parse date: ${dateString}`)
      return null
    } catch (e) {
      console.error(`Error parsing date ${dateString}:`, e)
      return null
    }
  }

  formatDate(date) {
    if (!date) return ''
    
    // Format date as YYYY-MM-DD
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    
    return `${year}-${month}-${day}`
  }
  
  updateAdminOverride(event) {
    // Update the hidden field value based on checkbox state
    const hiddenField = document.getElementById('admin_override_hidden')
    if (hiddenField) {
      hiddenField.value = event.target.checked ? '1' : ''
    }
  }

  checkAvailability(event) {
    event.preventDefault()

    // Validate dates first
    if (!this.validateDates()) {
      return
    }

    // Disable the submit button during check
    this.submitTarget.disabled = true
    this.submitTarget.innerHTML = '<i class="ph ph-spinner ph-spin mr-2 -ml-1 h-5 w-5"></i> Checking...'

    // Get form data
    const form = this.element.closest('form')
    const formData = new FormData(form)

    // Collect form data to preserve it
    if (this.hasGuestCountTarget) {
      formData.append('reservation[guest_count]', this.guestCountTarget.value)
    }
    
    if (this.hasSpecialRequestsTarget) {
      formData.append('reservation[special_requests]', this.specialRequestsTarget.value)
    }
    
    // Admin override: write to the field the server actually reads.
    // Rails form.check_box renders a hidden input pair (value="0") + visible checkbox
    // (value="1"). When this Stimulus controller serializes via FormData(form), the
    // hidden field wins. Explicitly .set() overrides it to honor the visible checkbox.
    if (this.hasAdminOverrideTarget && this.adminOverrideTarget.checked) {
      formData.set('reservation[override_minimum_stay]', '1')
    }

    // Submit the form via AJAX
    fetch(form.action + '/check_availability', {
      method: 'POST',
      headers: {
        'Accept': 'text/vnd.turbo-stream.html',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
      body: formData
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok')
      }
      return response.text()
    })
    .then(html => {
      // Process the Turbo Stream response
      Turbo.renderStreamMessage(html)
      
      // Update submit button state after Turbo Stream processing
      setTimeout(() => this.updateSubmitButtonState(), 100)
      
      // Re-enable the check availability button
      if (this.hasSubmitTarget) {
        this.submitTarget.disabled = false
        this.submitTarget.innerHTML = '<i class="ph ph-calendar-check mr-2 -ml-1 h-5 w-5"></i> Check Availability'
      }
      // Show submit button if available
      const stateDiv = this.stateTarget.querySelector('div')
      if (stateDiv && stateDiv.dataset.available === 'true' && this.hasSubmitButtonTarget) {
        this.submitButtonTarget.disabled = false
      } else if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.disabled = true
      }
    })
    .catch(error => {
      console.error('Error checking availability:', error)
      this.messageTarget.innerHTML = `
        <div class="rounded-md bg-red-50 p-4 mb-4">
            <div class="flex">
              <div class="flex-shrink-0">
                <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
              </svg>
              </div>
              <div class="ml-3">
                <p class="text-sm font-medium text-red-800">
                  Error checking availability
                </p>
                <p class="text-sm text-red-700 mt-1">
                  Please try again or contact support if the problem persists.
                </p>
              </div>
            </div>
          </div>
      `
      if (this.hasSubmitTarget) {
        this.submitTarget.disabled = false
        this.submitTarget.innerHTML = 'Check Availability'
      }
      if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.disabled = true
      }
    })
  }

  updateSubmitButtonState() {
    if (!this.hasStateTarget || !this.hasSubmitButtonTarget) return
    
    const stateDiv = this.stateTarget.querySelector('div')
    if (stateDiv) {
      const isAvailable = stateDiv.dataset.available === 'true'
      this.submitButtonTarget.disabled = !isAvailable
      
      // Log the state for debugging
      console.log(`Availability state updated: ${isAvailable ? 'Available' : 'Not available'}`)
    }
  }
}